import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

// Remove the local getCurrentUserId - we should use the one from utils/getCurrentUserId.ts
// to avoid duplication and confusion

/**
 * Get the current organization ID from Clerk
 */
export const getCurrentOrgId = (): string | null => {
  // This will be passed from components using useOrganization hook
  return null;
};

/**
 * @deprecated Legacy workspace creation - Clerk organizations ARE our workspaces now
 *
 * IMPORTANT: We've migrated to using Clerk organizations as our workspace system.
 * Clerk organizations provide all the workspace functionality we need:
 * - Multi-user collaboration
 * - Permission management
 * - Organization switching
 *
 * This function is kept for backwards compatibility but returns null to prevent
 * creating duplicate workspace records. Projects now use clerk_org_id directly.
 */
export const createDefaultWorkspaceForClerkUser = async (
  clerkUserId: string,
  clerkOrgId: string,
  userName?: string,
): Promise<{ workspaceId: string } | null> => {
  logger.log('Skipping legacy workspace creation - using Clerk org as workspace:', { clerkOrgId });

  // Return null since Clerk organizations ARE our workspaces
  // Projects should use clerk_org_id directly
  return null;
};

/**
 * Update Clerk organization mapping for existing workspaces
 */
export const updateClerkOrgMapping = async (
  workspaceName: string,
  clerkOrgId: string,
  clerkUserId: string,
): Promise<boolean> => {
  try {
    logger.log('Updating Clerk org mapping:', { workspaceName, clerkOrgId });
    const authSb = await getAuthenticatedSupabaseClient();

    // Check if mapping already exists for this org
    const { data: existingMapping } = await authSb
      .from('clerk_org_mappings')
      .select('workspace_id')
      .eq('clerk_org_id', clerkOrgId)
      .maybeSingle();

    if (existingMapping) {
      logger.log('Mapping already exists for Clerk org:', clerkOrgId);
      return true; // Already mapped
    }

    // Find workspace by name
    const { data: workspace, error: wsError } = await authSb
      .from('workspaces')
      .select('id')
      .eq('name', workspaceName)
      .maybeSingle();

    if (wsError || !workspace) {
      logger.log('Workspace not found or error:', workspaceName, wsError);
      return false;
    }

    // Delete any existing mapping for this workspace
    await authSb.from('clerk_org_mappings').delete().eq('workspace_id', workspace.id);

    // Create new mapping
    const { error: mapError } = await authSb.from('clerk_org_mappings').insert({
      workspace_id: workspace.id,
      clerk_org_id: clerkOrgId,
      clerk_user_id: clerkUserId,
      supabase_user_id: null,
    });

    if (mapError) {
      logger.error('Error creating mapping:', mapError);
      return false;
    }

    // Update all projects with the new clerk_org_id
    const { error: projError } = await authSb
      .from('projects')
      .update({ clerk_org_id: clerkOrgId })
      .eq('workspace_id', workspace.id);

    if (projError) {
      logger.error('Error updating projects:', projError);
      return false;
    }

    logger.log('Successfully updated Clerk org mapping for workspace:', workspace.id);
    return true;
  } catch (error) {
    logger.error('Error in updateClerkOrgMapping:', error);
    return false;
  }
};

/**
 * Get workspaces for a Clerk organization
 */
export const getWorkspacesForClerkOrg = async (clerkOrgId: string): Promise<any[]> => {
  try {
    const authSb = await getAuthenticatedSupabaseClient();

    // Simple mapping check - only use clerk_org_id
    const { data: mapping, error: mapError } = await authSb
      .from('clerk_org_mappings')
      .select('workspace_id, clerk_org_id')
      .eq('clerk_org_id', clerkOrgId);

    if (mapError) {
      logger.error('Error fetching org mapping:', mapError);
      return [];
    }

    if (!mapping || mapping.length === 0) {
      logger.log('No workspaces found for Clerk org:', clerkOrgId);
      return [];
    }

    // Get the workspaces (filter nulls — workspace_id is nullable in clerk_org_mappings)
    const workspaceIds = mapping.map(m => m.workspace_id).filter((id): id is string => !!id);
    if (workspaceIds.length === 0) {
      logger.log('No workspace_ids mapped for Clerk org:', clerkOrgId);
      return [];
    }
    const { data: workspaces, error: wsError } = await authSb
      .from('workspaces')
      .select('*')
      .in('id', workspaceIds);

    if (wsError) {
      logger.error('Error fetching workspaces:', wsError);
      return [];
    }

    return workspaces || [];
  } catch (error) {
    logger.error('Error in getWorkspacesForClerkOrg:', error);
    return [];
  }
};

/**
 * Get projects for a Clerk organization
 */
export const getProjectsForClerkOrg = async (clerkOrgId: string): Promise<any[]> => {
  try {
    const authSb = await getAuthenticatedSupabaseClient();

    // Directly query projects with the current org ID
    const orgIdToQuery = clerkOrgId;

    const { data: projects, error: projError } = await authSb
      .from('projects')
      .select('*')
      .eq('clerk_org_id', orgIdToQuery)
      .order('created_at', { ascending: false });

    if (projError) {
      logger.error('Error fetching projects:', projError);
      return [];
    }

    return projects || [];
  } catch (error) {
    logger.error('Error in getProjectsForClerkOrg:', error);
    return [];
  }
};

/**
 * Create a project for a Clerk organization
 */
export const createProjectForClerkOrg = async (
  clerkOrgId: string,
  projectData: {
    name: string;
    client?: string;
    location?: string;
    description?: string;
  },
): Promise<any | null> => {
  try {
    const authSb = await getAuthenticatedSupabaseClient();

    // Get workspace for this org
    const { data: mapping } = await authSb
      .from('clerk_org_mappings')
      .select('workspace_id')
      .eq('clerk_org_id', clerkOrgId)
      .maybeSingle();

    // Use the current org ID directly
    const orgIdToUse = clerkOrgId;
    const workspaceId = mapping?.workspace_id || null;

    const { data: project, error } = await authSb
      .from('projects')
      .insert({
        ...projectData,
        clerk_org_id: orgIdToUse,
        workspace_id: workspaceId,
      })
      .select('*')
      .maybeSingle();

    if (error) {
      logger.error('Error creating project:', error);
      return null;
    }

    return project;
  } catch (error) {
    logger.error('Error in createProjectForClerkOrg:', error);
    return null;
  }
};
